import { useEffect, useState } from "react";
import "./DiscoveryActivityTimeline.css";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Navigation from "./Navigation";
import { Helmet } from "react-helmet";
import { Honeybadger } from "@honeybadger-io/react";
import { RotatingLines } from "react-loader-spinner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const unauthorizedErrorMessage =
  "Нямате достъп до това съдържание. " +
  "Моля сканирайте QR кода и въведете вашия код за достъп. " +
  "Ако сте изгубили вашия код за достъп, моля свържете се с нас чрез линковете по-долу.";

function DiscoveryActivityTimeline() {
  const params = useParams();
  const themeID = params.themeID;
  const navigate = useNavigate();

  const [timelineData, setTimelineData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState();

  useEffect(() => {
    setIsLoading(true);

    fetch(`/api/themes/${themeID}/discoveries/stats/timeline/monthly`)
      .then((response) => {
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(unauthorizedErrorMessage);
          }
          throw new Error("Неочаквана грешка при заявка към системата");
        }
        return response.json();
      })
      .then((data) => {
        setTimelineData(data);
      })
      .catch((err) => {
        const errorMessage = err?.message || "Неочаквана грешка";

        Honeybadger.notify(
          "DiscoveryActivityTimeline page error while fetching timeline",
          {
            context: {
              themeID,
              error: errorMessage,
            },
          }
        );

        setError(errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [themeID]);

  const handleBackClick = () => {
    navigate(`/themes/${themeID}/discoveries/next`);
  };

  let mainElement = <div className="timeline-section-block"></div>;

  if (isLoading) {
    mainElement = (
      <div className="timeline-section-block">
        <RotatingLines
          visible={true}
          height="64"
          width="64"
          strokeColor="#fff75e"
          strokeWidth="5"
          animationDuration="1"
        />
      </div>
    );
  } else if (error) {
    mainElement = (
      <div className="timeline-section-block">
        <span className="timeline-error">{error}</span>
        <button className="timeline-back-button" onClick={handleBackClick}>
          Назад
        </button>
      </div>
    );
  } else if (timelineData) {
    mainElement = (
      <div className="timeline-section-block">
        <div className="timeline-section-header-block">
          <h4 className="timeline-header">Твоята активност</h4>

          <p className="timeline-summary-text">
            Общ брой{" "}
            {getCountUnit(
              timelineData.timeline.reduce(
                (sum, item) => sum + item.access_count,
                0
              ),
              themeID
            )}
            :{" "}
            {timelineData.timeline.reduce(
              (sum, item) => sum + item.access_count,
              0
            )}
          </p>
        </div>

        <ResponsiveContainer className="timeline-chart-container">
          <BarChart
            data={timelineData.timeline.map((item) => ({
              month: formatMonthLabel(item.year_month),
              accesses: item.access_count,
              fullMonth: item.year_month,
            }))}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: "#ffffffc0" }}
              axisLine={{ stroke: "#ffffffc0" }}
              tickLine={{ stroke: "#ffffffc0" }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#ffffffc0" }}
              axisLine={{ stroke: "#ffffffc0" }}
              tickLine={{ stroke: "#ffffffc0" }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                border: "1px solid #fff75e",
                borderRadius: "8px",
                color: "#ffffffc0",
              }}
              labelStyle={{ color: "#fff75e" }}
              formatter={(value, name, props) => [
                `${value} ` + getCountUnit(value, themeID),
                formatMonthLabel(props.payload.fullMonth),
              ]}
            />
            <Bar
              dataKey="accesses"
              fill="url(#timelineGradient)"
              radius={[6, 6, 0, 0]}
            />
            <defs>
              <linearGradient id="timelineGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fff75e" />
                <stop offset="100%" stopColor="#fdc43f" />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>

        <button className="timeline-back-button" onClick={handleBackClick}>
          Назад към съдържанието
        </button>
      </div>
    );
  }

  return (
    <section className="section timeline-section">
      <Helmet>
        <title>QR подаръци - история на достъпите - Теонибайт ЕООД</title>
        <meta
          name="description"
          content="Виж историята на твоите достъпи до ежедневното съдържание. Графика с активността ти през последните месеци."
        />
      </Helmet>

      <Navigation withLogoLink isCentered />

      {mainElement}

      <div className="timeline-footer-block">
        <Footer />
      </div>
    </section>
  );
}

function formatMonthLabel(yearMonth) {
  const [year, month] = yearMonth.split("-");
  const monthNames = [
    "Януари",
    "Февруари",
    "Март",
    "Април",
    "Май",
    "Юни",
    "Юли",
    "Август",
    "Септември",
    "Октомври",
    "Ноември",
    "Декември",
  ];
  return `${monthNames[parseInt(month) - 1]} ${year}`;
}

function getCountUnit(count, themeID) {
  switch (themeID) {
    case "t1l5g8":
      return count === 1 ? "късметче" : "късметчета";
    case "b4x7m3":
      return count === 1 ? "интересен факт" : "интересни факта";
    default:
      return count === 1 ? "сканиране" : "сканирания";
  }
}

export default DiscoveryActivityTimeline;
